import { departmentsService } from '../features/departments/services/departments.service';
import { staffsService } from '../features/properties/services/staffs.service';
import { companiesService } from '../features/properties/services/companies.service';
import { prefecturesService } from '../features/properties/services/prefectures.service';

import type { Staff, Department } from '@/features/companies/types/department.types';

import { SearchConfig, SearchOption } from '@/types/search';
import {
  BUSINESS_PARTNER_LABELS,
  CUSTOMER_SUPPLIER_LABELS,
  PARTNER_CLASSIFICATIONS,
} from '@/lib/constants';
import { FunctionName } from '@/lib/enums';
import { getTodayDateString } from '@/utils/date-time';
import {
  getPlanNameOptions,
  getPropertyOptions,
  getWitnessCompanyOptions,
} from '@/features/plans/components/materials/autocomplete-options';

// Options for 会社区分 (Company Classification)
export const getCompanyClassificationOptions = () => [
  { key: '', value: '', label: '指定なし' },
  ...Object.entries(BUSINESS_PARTNER_LABELS).map(([value, label]) => ({
    key: value,
    value,
    label,
  })),
];

// Options for 得意先/仕入先カテゴリ (Customer/Supplier Category)
export const getCustomerSupplierCategoryOptions = () => [
  { key: '', value: '', label: '指定なし' },
  ...Object.entries(CUSTOMER_SUPPLIER_LABELS).map(([value, label]) => ({
    key: value,
    value,
    label,
  })),
];

/**
 * Get construction company options from API (建設会社)
 * Partner classification = 3
 * @returns Promise with array of SearchOption
 */
const getConstructionCompanyOptions = async (
  functionName: FunctionName
): Promise<SearchOption[]> => {
  try {
    // Fetch construction companies (classification = 3)
    const companies = await companiesService.getCompaniesByClassification(
      PARTNER_CLASSIFICATIONS.CONSTRUCTION_COMPANY,
      functionName
    );

    // Transform to SearchOption format (no default empty option for autocomplete)
    const options = companies.map((company) => ({
      key: company.id,
      value: company.name,
      label: company.name,
    }));

    return [{ key: '', value: '', label: '指定なし' }, ...options];
  } catch (error) {
    console.error('Error fetching construction company options:', error);
    return [{ key: '', value: '', label: '指定なし' }];
  }
};

/**
 * Get customer options from API (得意先)
 * Partner classification = 1
 * @returns Promise with array of SearchOption
 */
const getCustomerOptions = async (functionName: FunctionName): Promise<SearchOption[]> => {
  try {
    // Fetch customers (classification = 1)
    const companies = await companiesService.getCompaniesByClassification(
      PARTNER_CLASSIFICATIONS.CUSTOMER,
      functionName
    );

    // Transform to SearchOption format (no default empty option for autocomplete)
    const options = companies.map((company) => ({
      key: company.id,
      value: company.name,
      label: company.name,
    }));

    return [{ key: '', value: '', label: '指定なし' }, ...options];
  } catch (error) {
    console.error('Error fetching customer options:', error);
    return [{ key: '', value: '', label: '指定なし' }];
  }
};

/**
 * Get sales staff options from API (営業)
 * Returns all options filtered by department if provided
 * @param departmentId - Optional department ID to filter staff
 * @param functionName - Function name
 * @returns Promise with array of SearchOption
 */
const getSalesStaffOptions = async (
  departmentId?: string,
  functionName?: FunctionName
): Promise<SearchOption[]> => {
  try {
    // Build params with optional department_id
    const params: { role_key: string; department_id?: string; function_name?: FunctionName } = {
      role_key: 'sale',
    };
    if (departmentId) {
      params.department_id = departmentId;
    }
    if (functionName) {
      params.function_name = functionName;
    }
    // Fetch sale staffs from API
    const staffs = await staffsService.getStaffs(params);

    // Cache staff data for name->id lookup (for staff filtering)
    const cacheKey = departmentId || 'all';
    cachedSalesStaffData[cacheKey] = staffs;

    // Transform to SearchOption format
    // Use full name as value for search API (text-based search)
    const options = staffs.map((staff) => {
      const fullName = `${staff.lastName} ${staff.firstName}`;
      return {
        key: staff.id,
        value: fullName,
        label: fullName,
      };
    });

    // Add default empty option
    return [{ key: '', value: '', label: '指定なし' }, ...options];
  } catch (error) {
    console.error('Error fetching sales staff options:', error);
    return [{ key: '', value: '', label: '指定なし' }];
  }
};

/**
 * Fetch department options from API (担当部署)
 * @returns Promise with array of SearchOption
 */
export const getDepartmentOptions = async (functionName: FunctionName): Promise<SearchOption[]> => {
  try {
    // Fetch departments from API
    const departments = await departmentsService.getDepartments(functionName);

    // Cache department data for lookup (for staff filtering by ID)
    cachedDepartments = departments;

    // Transform to SearchOption format
    // Use dept.name as value for search API (text-based search)
    const options = departments.map((dept) => ({
      key: dept.id,
      value: dept.name,
      label: dept.name,
    }));

    // Add default "指定なし" option at the beginning
    return [{ key: '', value: '', label: '指定なし' }, ...options];
  } catch (error) {
    console.error('Error fetching department options:', error);
    return [{ key: '', value: '', label: '指定なし' }];
  }
};

/**
 * Get delivery staff options from API (デリバリー)
 * Includes both 'delivery' and 'outsourcing_delivery' roles
 * Returns all options filtered by department if provided
 * @param departmentId - Optional department ID to filter staff
 * @param functionName - Function name
 * @returns Promise with array of SearchOption
 */
const getDeliveryStaffOptions = async (
  departmentId?: string,
  functionName?: FunctionName
): Promise<SearchOption[]> => {
  try {
    // Build params with optional department_id
    const deliveryParams: {
      role_key: string;
      department_id?: string;
      function_name?: FunctionName;
    } = { role_key: 'delivery' };
    const outsourcingParams: {
      role_key: string;
      department_id?: string;
      function_name?: FunctionName;
    } = {
      role_key: 'outsourcing_delivery',
    };

    if (departmentId) {
      deliveryParams.department_id = departmentId;
      outsourcingParams.department_id = departmentId;
    }

    if (functionName) {
      deliveryParams.function_name = functionName;
      outsourcingParams.function_name = functionName;
    }

    // Fetch both delivery and outsourcing_delivery staffs in parallel
    const [deliveryStaffs, outsourcingStaffs] = await Promise.all([
      staffsService.getStaffs(deliveryParams),
      staffsService.getStaffs(outsourcingParams),
    ]);

    // Combine and deduplicate by staff ID
    const combinedStaffs = [...deliveryStaffs, ...outsourcingStaffs];
    const uniqueStaffs = Array.from(
      new Map(combinedStaffs.map((staff) => [staff.id, staff])).values()
    );

    // Cache staff data for name->id lookup (for staff filtering)
    const cacheKey = departmentId || 'all';
    cachedDeliveryStaffData[cacheKey] = uniqueStaffs;

    // Transform to SearchOption format
    // Use full name as value for search API (text-based search)
    const options = uniqueStaffs.map((staff) => {
      const fullName = `${staff.lastName} ${staff.firstName}`;
      return {
        key: staff.id,
        value: fullName,
        label: fullName,
      };
    });

    // Add default empty option
    return [{ key: '', value: '', label: '指定なし' }, ...options];
  } catch (error) {
    console.error('Error fetching delivery staff options:', error);
    return [{ key: '', value: '', label: '指定なし' }];
  }
};

/**
 * Get prefecture options for dropdown from API
 * @returns Array of SearchOption with prefectures from API
 */
export const getPrefectureOptions = async (): Promise<SearchOption[]> => {
  try {
    const prefectures = await prefecturesService.getAll();

    const options = prefectures.map((prefecture) => ({
      key: prefecture.id,
      value: prefecture.name,
      label: prefecture.name,
    }));

    return [{ key: '', value: '', label: '指定なし' }, ...options];
  } catch (error) {
    console.error('Error loading prefectures:', error);
    return [{ key: '', value: '', label: '指定なし' }];
  }
};

// Cache for async loaded options
// Cache property options by function name and archive status
const cachedPropertyOptions: Record<string, SearchOption[]> = {};
// Cache for prefecture options
let cachedPrefectureOptions: SearchOption[] | null = null;
let cachedDepartmentOptions: SearchOption[] | null = null;
let cachedConstructionCompanyOptions: SearchOption[] | null = null;
let cachedCustomerOptions: SearchOption[] | null = null;
let cachedWitnessCompanyOptions: SearchOption[] | null = null;
let cachedPlanNameOptions: SearchOption[] | null = null;
// Cache staff options by department (key: department_id or 'all')
export const cachedSalesStaffByDept: Record<string, SearchOption[]> = {};
export const cachedDeliveryStaffByDept: Record<string, SearchOption[]> = {};
// Cache department data for id->name lookup
let cachedDepartments: Department[] = [];
// Cache staff data for code->name lookup (by role and department)
const cachedSalesStaffData: Record<string, Staff[]> = {};
const cachedDeliveryStaffData: Record<string, Staff[]> = {};

const clearRecordCache = <T>(cache: Record<string, T>): void => {
  Object.keys(cache).forEach((key) => {
    delete cache[key];
  });
};

/**
 * Clear all cached staff options/data across departments.
 * Use this after member role updates so dropdown data is re-fetched immediately.
 */
export const clearAllStaffOptionCaches = (): void => {
  clearRecordCache(cachedSalesStaffByDept);
  clearRecordCache(cachedDeliveryStaffByDept);
  clearRecordCache(cachedSalesStaffData);
  clearRecordCache(cachedDeliveryStaffData);
};

const buildPropertyOptionsCacheKey = (functionName: FunctionName, hasArchived: boolean): string =>
  `${functionName}:${hasArchived ? 'archived' : 'active'}`;

const isArchiveFilterEnabled = (value: unknown): boolean => {
  return value === true || value === 'true' || value === 1 || value === '1';
};

/**
 * Load prefecture options with caching
 * @param _query - Search query string (not used for this implementation)
 * @param _context - Context object containing form values (not used for this implementation)
 * @returns Promise with prefecture options
 */
export const loadPrefectureOptions = async (
  _query?: string,
  _context?: Record<string, any>
): Promise<SearchOption[]> => {
  if (cachedPrefectureOptions) {
    return cachedPrefectureOptions;
  }

  const options = await getPrefectureOptions();
  cachedPrefectureOptions = options;
  return options;
};

/**
 * Load and cache construction company options
 * @param _query - Search query string (not used for this implementation)
 * @param _context - Context object containing form values (not used for this implementation)
 */
export const loadConstructionCompanyOptions = async (
  functionName: FunctionName,
  _query?: string,
  _context?: Record<string, any>
): Promise<SearchOption[]> => {
  if (cachedConstructionCompanyOptions) {
    return cachedConstructionCompanyOptions;
  }
  cachedConstructionCompanyOptions = await getConstructionCompanyOptions(functionName);
  return cachedConstructionCompanyOptions;
};

export const loadWitnessCompanyOptions = async (
  functionName: FunctionName
): Promise<SearchOption[]> => {
  if (cachedWitnessCompanyOptions) {
    return cachedWitnessCompanyOptions;
  }
  cachedWitnessCompanyOptions = await getWitnessCompanyOptions(functionName);
  return cachedWitnessCompanyOptions;
};

export const loadPlanNameOptions = async (functionName: FunctionName): Promise<SearchOption[]> => {
  if (cachedPlanNameOptions) {
    return cachedPlanNameOptions;
  }
  cachedPlanNameOptions = await getPlanNameOptions(functionName);
  return cachedPlanNameOptions;
};

/**
 * Load and cache customer options
 * @param _query - Search query string (not used for this implementation)
 * @param _context - Context object containing form values (not used for this implementation)
 */
export const loadCustomerOptions = async (
  functionName: FunctionName,
  _query?: string,
  _context?: Record<string, any>
): Promise<SearchOption[]> => {
  if (cachedCustomerOptions) {
    return cachedCustomerOptions;
  }
  cachedCustomerOptions = await getCustomerOptions(functionName);
  return cachedCustomerOptions;
};

/**
 * Load and cache department options
 * @param _query - Search query string (not used for this implementation)
 * @param _context - Context object containing form values (not used for this implementation)
 */
export const loadDepartmentOptions = async (
  _query?: string,
  context?: Record<string, any>
): Promise<SearchOption[]> => {
  // Extract functionName from context or use default
  const functionName = (context?.functionName as FunctionName) || FunctionName.PROPERTY_MANAGEMENT;

  if (cachedDepartmentOptions) {
    return cachedDepartmentOptions;
  }
  cachedDepartmentOptions = await getDepartmentOptions(functionName);
  return cachedDepartmentOptions;
};

/**
 * Load and cache sales staff options by department
 * @param _query - Search query string (not used for this implementation)
 * @param context - Context object containing form values (e.g., department)
 * @returns Promise with array of SearchOption
 */
export const loadSalesStaffOptions = async (
  _query?: string,
  context?: Record<string, any>,
  functionName?: FunctionName
): Promise<SearchOption[]> => {
  // Get department name from context and convert to ID
  const departmentName = context?.department;
  const departmentId = departmentName ? getDepartmentIdByName(departmentName) : undefined;

  const cacheKey = departmentId || 'all';

  // Check cache first - return cached options if available
  if (cachedSalesStaffByDept[cacheKey]) {
    return cachedSalesStaffByDept[cacheKey];
  }

  // Fetch and cache
  const options = await getSalesStaffOptions(departmentId, functionName);
  cachedSalesStaffByDept[cacheKey] = options;
  return options;
};

/**
 * Load and cache delivery staff options by department
 * @param _query - Search query string (not used for this implementation)
 * @param context - Context object containing form values (e.g., department)
 * @returns Promise with array of SearchOption
 */
export const loadDeliveryStaffOptions = async (
  _query?: string,
  context?: Record<string, any>,
  functionName?: FunctionName
): Promise<SearchOption[]> => {
  // Get department name from context and convert to ID
  const departmentName = context?.department;
  const departmentId = departmentName ? getDepartmentIdByName(departmentName) : undefined;

  const cacheKey = departmentId || 'all';

  // Check cache first - return cached options if available
  if (cachedDeliveryStaffByDept[cacheKey]) {
    return cachedDeliveryStaffByDept[cacheKey];
  }

  // Fetch and cache
  const options = await getDeliveryStaffOptions(departmentId, functionName);
  cachedDeliveryStaffByDept[cacheKey] = options;
  return options;
};

/**
 * Load and cache property options by archive status
 * @param _query - Search query string (not used for this implementation)
 * @param context - Context object containing form values (e.g., includeArchive)
 * @returns Promise with property options
 */
export const loadPropertyOptions = async (
  functionName: FunctionName,
  _query?: string,
  context?: Record<string, any>
): Promise<SearchOption[]> => {
  const archiveFilter = context?.includeArchive ?? context?.isArchive ?? false;
  const hasArchived = isArchiveFilterEnabled(archiveFilter);
  const cacheKey = buildPropertyOptionsCacheKey(functionName, hasArchived);

  // Return from cache if available
  if (cachedPropertyOptions[cacheKey]) {
    return cachedPropertyOptions[cacheKey];
  }

  // Fetch and cache
  const options = await getPropertyOptions(functionName, hasArchived);
  cachedPropertyOptions[cacheKey] = options;
  return options;
};

/**
 * Reload property options when archive status changes
 * This clears the cache and fetches fresh data
 * @param hasArchived - Include archived properties
 */
export const reloadPropertyOptions = async (
  functionName: FunctionName,
  hasArchived: boolean = false
): Promise<void> => {
  try {
    const cacheKey = buildPropertyOptionsCacheKey(functionName, hasArchived);

    // Clear cache for this archive status
    delete cachedPropertyOptions[cacheKey];

    // Fetch fresh data with context object
    await loadPropertyOptions(functionName, '', {
      includeArchive: hasArchived,
      isArchive: hasArchived,
    });

    // Update the field options in searchConfigs
    const isPlansFunction = functionName === FunctionName.SCHEDULE_MANAGEMENT;
    const fields = isPlansFunction ? searchConfigs.plans.fields : searchConfigs.properties.fields;
    const propertyFieldKey = isPlansFunction ? 'propertyName' : 'siteName';
    const propertyField = fields.find((f) => f.key === propertyFieldKey);
    if (propertyField?.options) {
      propertyField.options = cachedPropertyOptions[cacheKey] || [];
    }
  } catch (error) {
    console.error('Error reloading property options:', error);
  }
};

// Helper functions to create field configurations
const createSelectField = (
  key: string,
  label: string,
  options: SearchOption[],
  defaultValue: string = ''
) => ({
  key,
  label,
  type: 'select' as const,
  options,
  defaultValue,
  required: false,
});

const createTextField = (key: string, label: string, placeholder: string) => ({
  key,
  label,
  type: 'text' as const,
  placeholder,
  required: false,
});

const createAutocompleteField = (
  key: string,
  label: string,
  fetchOptions: (query: string, context?: Record<string, any>) => Promise<SearchOption[]>,
  options: SearchOption[] = [],
  dependsOn?: string[],
  placeholder?: string
) => ({
  key,
  label,
  type: 'autocomplete' as const,
  options,
  fetchOptions,
  freeSolo: true,
  required: false,
  ...(dependsOn && { dependsOn }),
  ...(placeholder && { placeholder }),
});

// Search configurations for different pages
export const searchConfigs = {
  // Properties (Sites) (default layout preserved)
  properties: {
    fields: [
      {
        ...createAutocompleteField(
          'siteName',
          '現場',
          (event, context) => loadPropertyOptions(FunctionName.PROPERTY_MANAGEMENT, event, context),
          [],
          ['includeArchive']
        ),
        showOnMobile: true,
        mobileOverride: {
          type: 'text',
          label: '現場略称',
        },
      },
      {
        ...createAutocompleteField(
          'constructionCompany',
          '建設会社',
          () => loadConstructionCompanyOptions(FunctionName.PROPERTY_MANAGEMENT),
          [],
          []
        ),
        showOnMobile: true,
        mobileOverride: {
          type: 'text',
        },
      },
      {
        ...createAutocompleteField('client', '得意先', () =>
          loadCustomerOptions(FunctionName.PROPERTY_MANAGEMENT)
        ),
        showOnMobile: false,
      },
      {
        ...createAutocompleteField(
          'salesRepresentative',
          '営業',
          (query, context) =>
            loadSalesStaffOptions(query, context, FunctionName.PROPERTY_MANAGEMENT),
          [],
          ['department']
        ),
        showOnMobile: true,
        mobileOverride: {
          type: 'text',
        },
      },
      {
        ...createAutocompleteField(
          'department',
          '担当部署',
          (_query, _context) =>
            loadDepartmentOptions(_query, { functionName: FunctionName.PROPERTY_MANAGEMENT }),
          [],
          undefined,
          '入力ください'
        ),
        showOnMobile: false,
      },
      {
        ...createAutocompleteField(
          'delivery',
          'デリバリー',
          (query, context) =>
            loadDeliveryStaffOptions(query, context, FunctionName.PROPERTY_MANAGEMENT),
          [],
          ['department']
        ),
        showOnMobile: false,
      },
      {
        key: 'prefecture',
        label: '都道府県',
        type: 'autocomplete' as const,
        fetchOptions: loadPrefectureOptions,
        options: [],
        freeSolo: true,
        required: false,
        showOnMobile: false,
      },
      {
        key: 'constructionStartDate',
        label: '工期開始日',
        type: 'date' as const,
        required: false,
        placeholder: '選択してください',
        showOnMobile: true,
      },
      {
        key: 'constructionDateSeparator',
        label: '~',
        type: 'label' as const,
        required: false,
        showOnMobile: true,
      },
      {
        key: 'constructionEndDate',
        label: '工期終了日',
        type: 'date' as const,
        required: false,
        placeholder: '選択してください',
        showOnMobile: true,
      },
      {
        key: 'includeArchive',
        label: 'アーカイブを含む',
        type: 'checkbox' as const,
        required: false,
        showOnMobile: true,
        alwaysVisible: true,
      },
    ],
    // No layout specified - uses default behavior
    onSearch: () => {},
  } as SearchConfig,

  // Plans
  plans: {
    fields: [
      {
        ...createAutocompleteField(
          'propertyName',
          '現場略称',
          (query, context) => loadPropertyOptions(FunctionName.SCHEDULE_MANAGEMENT, query, context),
          [],
          ['isArchive']
        ),
        showOnMobile: true,
        mobileOverride: {
          type: 'text',
        },
      },
      {
        ...createAutocompleteField('constructionCompanyName', '建設会社', () =>
          loadConstructionCompanyOptions(FunctionName.SCHEDULE_MANAGEMENT)
        ),
        showOnMobile: true,
        mobileOverride: {
          type: 'text',
        },
      },
      {
        ...createAutocompleteField(
          'planName',
          'プラント',
          () => loadPlanNameOptions(FunctionName.SCHEDULE_MANAGEMENT),
          [],
          []
        ),
        showOnMobile: true,
        mobileOverride: {
          type: 'text',
        },
      },
      {
        ...createAutocompleteField(
          'witnessCompanyName',
          '立会会社',
          () => loadWitnessCompanyOptions(FunctionName.SCHEDULE_MANAGEMENT),
          [],
          []
        ),
        showOnMobile: true,
        mobileOverride: {
          type: 'text',
        },
      },
      {
        ...createAutocompleteField(
          'deliveryPersonName',
          'デリバリー',
          (query, context) =>
            loadDeliveryStaffOptions(query, context, FunctionName.SCHEDULE_MANAGEMENT),
          [],
          ['departmentName']
        ),
        showOnMobile: true,
        mobileOverride: {
          type: 'text',
        },
      },
      {
        ...createAutocompleteField(
          'personInChargeName',
          '営業',
          (query, context) =>
            loadSalesStaffOptions(query, context, FunctionName.SCHEDULE_MANAGEMENT),
          [],
          undefined,
          '入力ください'
        ),
        showOnMobile: true,
        mobileOverride: {
          type: 'text',
        },
      },
      {
        ...createAutocompleteField('departmentName', '担当部署', (_query, _context) =>
          loadDepartmentOptions(_query, { functionName: FunctionName.SCHEDULE_MANAGEMENT })
        ),
        showOnMobile: true,
        mobileOverride: {
          type: 'text',
        },
      },
      {
        ...createSelectField('orderStatus', 'ステータス', [
          { key: 'all', value: '1', label: '指定なし' },
          { key: 'previous', value: '2', label: '交渉中' },
          { key: 'done', value: '3', label: '手配済' },
        ]),
        showOnMobile: true,
      },
      {
        key: 'placementDateFrom',
        label: '予定日',
        type: 'date' as const,
        defaultValue: getTodayDateString(),
        required: false,
        showOnMobile: true,
      },
      {
        key: 'daySeparator',
        label: '~',
        type: 'label' as const,
        required: false,
        showOnMobile: true,
      },
      {
        key: 'placementDateTo',
        label: '終了日',
        type: 'date' as const,
        required: false,
        showOnMobile: true,
      },
      {
        key: 'isArchive',
        label: 'アーカイブを含む',
        type: 'checkbox' as const,
        required: false,
        showOnMobile: true,
        alwaysVisible: true,
      },
    ],
    layout: {
      type: 'grid',
      columns: 4, // Flexible grid layout: 4 columns for plans page (can be 2, 3, 4, etc.)
    },
    onSearch: () => {},
  } as SearchConfig,

  // Companies (default layout preserved)
  companies: {
    fields: [
      createSelectField('partnerClassification', '会社区分', getCompanyClassificationOptions()),
      createTextField('name', '名称', '入力ください'),
      createTextField('locationAddress1', '所在地住所', '入力ください'),
      createAutocompleteField(
        'saleRepresentativeName',
        '営業',
        (query, context) => loadSalesStaffOptions(query, context, FunctionName.COMPANY_MANAGEMENT),
        [],
        undefined,
        '入力ください'
      ),
      createSelectField(
        'customerCategory',
        '得意先/仕入先カテゴリ',
        getCustomerSupplierCategoryOptions()
      ),
    ],
    // No layout specified - uses default behavior
    onSearch: () => {},
  } as SearchConfig,

  // Members (default layout preserved)
  members: {
    fields: [
      createAutocompleteField(
        'department',
        '所属部署',
        (_query, _context) =>
          loadDepartmentOptions(_query, { functionName: FunctionName.STAFF_MANAGEMENT }),
        [],
        undefined,
        '入力ください'
      ),
      createTextField('name', '姓', '氏名で検索'),
      createSelectField('status', '状態', [
        { key: 'registered', value: '登録済', label: '登録済' },
        { key: 'deleted', value: '削除済', label: '削除済' },
      ]),
    ],
    // No layout specified - uses default behavior
    onSearch: () => {},
  } as SearchConfig,
};

/**
 * Initialize and load all async options for properties search config
 * This should be called when the component mounts
 * @param departmentName - Optional department name to filter staff
 * @param hasArchived - Include archived properties
 */
export const initializePropertiesSearchConfig = async (
  hasArchived: boolean = false
): Promise<void> => {
  try {
    // Load all options in parallel with proper signatures
    const [propertyOptions, prefectureOptions] = await Promise.all([
      loadPropertyOptions(FunctionName.PROPERTY_MANAGEMENT, '', { includeArchive: hasArchived }),
      loadPrefectureOptions(),
    ]);

    // Find and update the fields with loaded options
    const fields = searchConfigs.properties.fields;

    const propertyField = fields.find((f) => f.key === 'siteName');
    if (propertyField) {
      propertyField.options = propertyOptions;
    }

    const prefectureField = fields.find((f) => f.key === 'prefecture');
    if (prefectureField) {
      prefectureField.options = prefectureOptions;
    }
  } catch (error) {
    console.error('Error initializing search config options:', error);
  }
};

/**
 * Reload staff options when department changes
 * This updates only the staff fields without reloading department options
 * @param departmentName - Optional department name to filter staff
 */
export const reloadStaffOptions = async (
  departmentName?: string,
  functionName?: FunctionName
): Promise<void> => {
  try {
    // Clear cache first to force reload
    const cacheKey = departmentName ? getDepartmentIdByName(departmentName) || 'all' : 'all';
    delete cachedSalesStaffByDept[cacheKey];
    delete cachedDeliveryStaffByDept[cacheKey];

    // Build context object for staff filters
    const context = departmentName ? { department: departmentName } : undefined;

    // Load staff options for the selected department with proper signatures
    const [salesOptions, deliveryOptions] = await Promise.all([
      loadSalesStaffOptions('', context, functionName),
      loadDeliveryStaffOptions('', context, functionName),
    ]);

    // Update only staff fields
    const fields = searchConfigs.properties.fields;

    const salesField = fields.find((f) => f.key === 'salesRepresentative');
    if (salesField) {
      salesField.options = salesOptions;
    }

    const deliveryField = fields.find((f) => f.key === 'delivery');
    if (deliveryField) {
      deliveryField.options = deliveryOptions;
    }
  } catch (error) {
    console.error('Error reloading staff options:', error);
  }
};

/**
 * Get department ID from department name
 * @param departmentName - Department name (string)
 * @returns Department ID (UUID) or undefined if not found
 */
export const getDepartmentIdByName = (departmentName: string | undefined): string | undefined => {
  if (!departmentName) return undefined;
  const department = cachedDepartments.find((dept) => dept.name === departmentName);
  return department?.id;
};

/**
 * Get department code from department name
 * @param departmentName - Department name (string)
 * @returns Department code (number) or undefined if not found
 */
export const getDepartmentCodeByName = (departmentName: string | undefined): number | undefined => {
  if (!departmentName) return undefined;
  const department = cachedDepartments.find((dept) => dept.name === departmentName);
  return department?.code;
};
/**
 * Helper function to get search config by page
 * Note: For properties config, call initializePropertiesSearchConfig() first
 * to load async options
 */
export const getSearchConfig = (page: keyof typeof searchConfigs): SearchConfig => {
  return searchConfigs[page];
};
